import { useEffect, useState } from 'react';
import { ArrowLeft, Play } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { EmptyState, LoadingRows } from '../components/StateViews';
import { fetchSeries, fetchSeriesDetail } from '../lib/firestore';
import type { EpisodeItem, SeriesItem } from '../types/content';

function ShowsIndex() {
  const [items, setItems] = useState<SeriesItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchSeries()
      .then(setItems)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingRows label="Loading shows" />;
  if (error) return <EmptyState title="Shows unavailable" body="Published series could not be loaded right now." />;
  if (items.length === 0) return <EmptyState title="No shows yet" body="Published series will appear here." />;

  return (
    <section className="shows-grid">
      <div className="section-heading">
        <p className="eyebrow">Shows</p>
        <h1>Pick a world, then keep moving.</h1>
      </div>
      {items.map((item, index) => (
        <Link className={index === 0 ? 'show-card show-card-hero' : 'show-card'} key={item.id} to={`/shows/${item.id}`}>
          <img src={item.posterUrl || item.bannerUrl} alt="" />
          <div>
            <p>{item.genres[0] || 'Series'}</p>
            <h2>{item.title}</h2>
            <span>{item.description || `${item.totalSeasons} season${item.totalSeasons === 1 ? '' : 's'}`}</span>
          </div>
        </Link>
      ))}
    </section>
  );
}

function SeriesDetail({ seriesId }: { seriesId: string }) {
  const [series, setSeries] = useState<SeriesItem | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchSeriesDetail(seriesId)
      .then((detail) => {
        setSeries(detail.series);
        setEpisodes(detail.episodes);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [seriesId]);

  if (loading) return <LoadingRows label="Loading show detail" />;
  if (error || !series) return <EmptyState title="Show unavailable" body="This series could not be loaded." />;

  return (
    <section className="series-detail">
      <Link className="icon-link" to="/shows" aria-label="Back to shows">
        <ArrowLeft size={21} />
      </Link>
      <div className="series-hero">
        <img src={series.bannerUrl || series.posterUrl} alt="" />
        <div className="series-hero-copy">
          <p className="eyebrow">{series.genres.slice(0, 2).join(' / ') || 'InstaFlix'}</p>
          <h1>{series.title}</h1>
          <p>{series.longDescription || series.description}</p>
        </div>
      </div>
      <div className="episode-list">
        <h2>Episodes</h2>
        {episodes.length === 0 ? (
          <p className="muted">Episodes will appear after publishing.</p>
        ) : (
          episodes.map((episode) => (
            <article className="episode-row" key={episode.id}>
              <img src={episode.thumbnailUrl || series.posterUrl} alt="" />
              <div>
                <span>E{episode.episodeNumber.toString().padStart(2, '0')}</span>
                <h3>{episode.title}</h3>
                <p>{episode.description}</p>
              </div>
              <Play size={18} fill="currentColor" />
            </article>
          ))
        )}
      </div>
    </section>
  );
}

export function ShowsPage() {
  const { seriesId } = useParams();
  return <div className="page-pad">{seriesId ? <SeriesDetail seriesId={seriesId} /> : <ShowsIndex />}</div>;
}
